import React from 'react';
import { Link } from 'react-router-dom';
import {
  FiArrowLeft,
  FiExternalLink,
  FiShield,
  FiCreditCard,
  FiLink,
  FiSend,
  FiSmartphone,
  FiUsers,
  FiArrowRight,
} from 'react-icons/fi';
import SEO from '../../components/SEO';
import './CaseStudy.css';

/**
 * PagomigoCaseStudy — /portfolio/pagomigo. Follows the same
 * CaseStudy.css scaffolding as the other case studies; only the
 * accent color and copy differ.
 */
const ACCENT = '#1E40AF';
const LIVE_URL = 'https://www.pagomigo.com/';
const IMG_MAC = 'https://silverfoxmedia.s3.amazonaws.com/pagomigohpv2-front.png';

const PagomigoCaseStudy = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'Pagomigo — Case Study',
    headline: 'Fintech Digital Wallet with Full KYC Onboarding',
    description:
      'A consumer digital wallet — P2P transfers, issued debit cards, QR / NFC payments, and bill pay — with Persona KYC identity verification, Plaid bank linking, and Unit banking-as-a-service under the hood.',
    image: IMG_MAC,
    url: 'https://silverfoxmedia.co/portfolio/pagomigo',
    author: {
      '@type': 'Organization',
      name: 'SilverFox Media',
      url: 'https://silverfoxmedia.co',
    },
    about: [
      { '@type': 'Thing', name: 'Digital Wallet' },
      { '@type': 'Thing', name: 'KYC / Identity Verification' },
      { '@type': 'Thing', name: 'Plaid Bank Linking' },
      { '@type': 'Thing', name: 'Unit Banking APIs' },
    ],
  };

  return (
    <>
      <SEO
        title="Pagomigo Case Study: Fintech Digital Wallet with KYC"
        description="How SilverFox Media designed and shipped Pagomigo: a consumer fintech wallet with P2P transfers, issued debit cards, QR / NFC payments, bill pay, Persona KYC, Plaid bank linking, and Unit banking APIs. Regulated fintech patterns end-to-end."
        keywords="Pagomigo case study, fintech wallet, Persona KYC, Plaid Auth, Unit banking APIs, P2P money transfer, digital debit card, MERN stack fintech, KYC AML onboarding"
        image="https://silverfoxmedia.co/images/og/pagomigo.jpg"
        url="/portfolio/pagomigo"
        type="article"
        schema={structuredData}
      />

      <article
        className="case-study"
        style={{ '--case-study-accent': ACCENT }}
      >
        {/* HERO */}
        <section className="case-study__hero">
          <div className="container">
            <div className="case-study__hero-grid">
              <div className="case-study__hero-inner">
                <Link to="/portfolio" className="case-study__back">
                  <FiArrowLeft />
                  Back to Portfolio
                </Link>

                <span className="case-study__category">Fintech / Digital Wallet</span>

                <h1 className="case-study__title">
                  Pagomigo:{' '}
                  <span className="case-study__title-accent">
                    A Digital Wallet Built for Real Money
                  </span>
                </h1>

                <p className="case-study__tagline">
                  A consumer fintech wallet — send cash to friends, pay bills, tap
                  your phone to pay in-store, and manage everything from an issued
                  debit card, all in one app. Every user is identity-verified and
                  bank-linked before any money moves. Regulated fintech patterns
                  end-to-end.
                </p>

                <div className="case-study__hero-cta">
                  <a
                    href={LIVE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="case-study__cta-primary"
                  >
                    Visit Live Site <FiExternalLink />
                  </a>
                  <Link to="/contact" className="case-study__cta-secondary">
                    Start Your Project <FiArrowRight />
                  </Link>
                </div>

                <div className="case-study__hero-meta">
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Client</span>
                    <span className="case-study__hero-meta-value">Pagomigo</span>
                  </div>
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Industry</span>
                    <span className="case-study__hero-meta-value">Fintech / Consumer Wallet</span>
                  </div>
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Timeline</span>
                    <span className="case-study__hero-meta-value">2024 — Live</span>
                  </div>
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Role</span>
                    <span className="case-study__hero-meta-value">Full-Stack Build</span>
                  </div>
                </div>
              </div>

              <div className="case-study__hero-media">
                <img
                  src={IMG_MAC}
                  alt="Pagomigo homepage displayed on a MacBook mockup"
                  className="case-study__hero-macbook"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* FEATURED IMAGE */}
        <section className="case-study__featured">
          <div className="container">
            <div className="case-study__featured-image-wrap">
              <img
                src={IMG_MAC}
                alt="Pagomigo homepage displayed on a MacBook mockup"
                className="case-study__featured-image"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* OVERVIEW */}
        <section className="case-study__section">
          <div className="container">
            <p className="case-study__section-label">The Overview</p>
            <h2 className="case-study__section-title">
              A digital wallet where every dollar is tied to a verified person.
            </h2>
            <div className="case-study__prose">
              <p>
                Pagomigo is a consumer digital wallet built for people who move
                money the same way they text — quickly, in the moment, without
                walking into a branch. Sign up in minutes with an ID and a selfie,
                connect a bank account, send cash to friends by name or QR code,
                tap your phone to pay in-store, spend from your issued debit card
                anywhere Visa is accepted, and pay bills straight from the app.
              </p>
              <p>
                Under the hood, it's a regulated financial platform. Every user
                clears Persona KYC identity verification and watchlist screening
                before their wallet activates. Every linked bank account is
                verified against that Persona identity. Every dollar in and out
                is tied to a real, verified person — the same primitives every
                regulated fintech, sportsbook, and payments platform relies on.
              </p>
            </div>
          </div>
        </section>

        {/* CHALLENGE */}
        <section className="case-study__section" style={{ background: 'var(--color-gray-100)' }}>
          <div className="container">
            <p className="case-study__section-label">The Challenge</p>
            <h2 className="case-study__section-title">
              Ship a real wallet, not a demo — with real KYC and real bank rails.
            </h2>
            <div className="case-study__prose">
              <p>
                Building a consumer wallet in 2024 is not a design exercise. Every
                feature touches a compliance requirement, a payment rail, and a
                fraud vector at the same time. A user who's still onboarding sees
                a friendly signup flow; behind that flow, we're running government
                ID capture, selfie liveness, watchlist / sanctions screening, and
                bank ownership verification, and we're getting them all to agree
                before we mint a debit card in the user's name.
              </p>
              <p>
                The second problem was reducing friction without cutting the
                compliance. A wallet that takes 10 minutes to sign up churns 60%
                of prospects at the ID upload screen. So the onboarding had to be
                a hosted Persona Inquiry flow the user experiences as three
                photos, with decision routing on our side — approved wallets go
                live, declined go into an escalation queue, and needs-review land
                on an admin dashboard for a human to eyeball before we auto-approve.
              </p>
              <p>
                The third problem was the money-movement layer itself. Consumer
                wallets can't just call a Visa API — you need a bank-as-a-service
                partner to issue cards, run ACH, and clear P2P. Unit sits behind
                us for all of that, but you still have to design the flow so
                errors from any of the three vendors (Persona / Plaid / Unit)
                surface cleanly and don't leave the user stranded.
              </p>
            </div>
          </div>
        </section>

        {/* SOLUTION / KEY FEATURES */}
        <section className="case-study__section">
          <div className="container">
            <p className="case-study__section-label">The Solution</p>
            <h2 className="case-study__section-title">
              A MERN wallet with three vendors under one clean UX.
            </h2>
            <p className="case-study__section-lead">
              Pagomigo is a mobile-first PWA on the front, MongoDB + Express on
              the back, and three vendor integrations orchestrated behind a
              single onboarding funnel. Every state change is webhook-driven, so
              the wallet reacts in real time when Persona decisions land, Plaid
              accounts link, or Unit posts a transaction.
            </p>

            <div className="case-study__features">
              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiShield /></span>
                <h3 className="case-study__feature-title">Persona KYC Onboarding</h3>
                <p className="case-study__feature-body">
                  Hosted Persona Inquiry flow: government-ID capture (front + back),
                  selfie liveness check, and watchlist / sanctions screening.
                  Webhook-driven decision routing — approved wallets activate,
                  declined go to escalation, needs-review lands on the admin dashboard.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiLink /></span>
                <h3 className="case-study__feature-title">Plaid Bank Linking</h3>
                <p className="case-study__feature-body">
                  Plaid Link for account connection, Auth product for account +
                  routing numbers on ACH origination, Identity product to match
                  the bank account holder name against the Persona result (a
                  fraud gate), Balance for pre-transfer sufficiency checks.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiCreditCard /></span>
                <h3 className="case-study__feature-title">Unit Banking APIs</h3>
                <p className="case-study__feature-body">
                  Unit issues the debit cards, runs the ACH transfers in and out,
                  and posts transactions to the user's ledger. Real-time balance
                  queries and transaction webhooks keep the app's ledger view
                  synced with the underlying banking core.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiSend /></span>
                <h3 className="case-study__feature-title">P2P + QR + NFC Payments</h3>
                <p className="case-study__feature-body">
                  Send money to friends and family by name, phone number, or QR
                  code — instant when both users are on the wallet, ACH-backed
                  when the recipient is external. Tap-to-pay at checkout via QR
                  code and NFC on modern phones.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiSmartphone /></span>
                <h3 className="case-study__feature-title">Bill Pay + Real-Time Alerts</h3>
                <p className="case-study__feature-body">
                  Pay bills directly from the app. Real-time push and SendGrid
                  email notifications for every deposit, withdrawal, P2P
                  transfer, and KYC status change — users know the moment money
                  moves.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiUsers /></span>
                <h3 className="case-study__feature-title">Ops Dashboard</h3>
                <p className="case-study__feature-body">
                  Admin visibility into every account, transaction, and
                  verification status. Escalation queue for KYC decisions that
                  need human review. Rate limiting and abuse detection on account
                  creation to keep out synthetic identities.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* TECH STACK */}
        <section className="case-study__section" style={{ background: 'var(--color-gray-100)' }}>
          <div className="container">
            <p className="case-study__section-label">The Stack</p>
            <h2 className="case-study__section-title">MERN + PWA on the client, three vendors on the back.</h2>
            <p className="case-study__section-lead">
              Clean stack, purpose-picked vendors. Every dependency earns its place.
            </p>

            <div className="case-study__tech">
              <span className="case-study__tech-chip">React</span>
              <span className="case-study__tech-chip">PWA</span>
              <span className="case-study__tech-chip">Node.js</span>
              <span className="case-study__tech-chip">Express</span>
              <span className="case-study__tech-chip">MongoDB</span>
              <span className="case-study__tech-chip">Persona KYC</span>
              <span className="case-study__tech-chip">Plaid (Link, Auth, Identity, Balance)</span>
              <span className="case-study__tech-chip">Unit Banking APIs</span>
              <span className="case-study__tech-chip">SendGrid</span>
              <span className="case-study__tech-chip">Web Push</span>
              <span className="case-study__tech-chip">bcryptjs</span>
            </div>
          </div>
        </section>

        {/* RESULTS */}
        <section className="case-study__section case-study__section--dark">
          <div className="container">
            <p className="case-study__section-label">The Results</p>
            <h2 className="case-study__section-title">A live wallet, verified identity end-to-end.</h2>
            <p className="case-study__section-lead" style={{ color: 'rgba(255,255,255,0.75)' }}>
              Pagomigo is live at pagomigo.com — a shipped consumer fintech
              wallet with the same identity, banking, and compliance patterns
              regulated payments platforms rely on.
            </p>

            <div className="case-study__stats">
              <div className="case-study__stat">
                <p className="case-study__stat-value">3 vendors</p>
                <p className="case-study__stat-label">Persona + Plaid + Unit orchestrated behind one onboarding funnel</p>
              </div>
              <div className="case-study__stat">
                <p className="case-study__stat-value">100%</p>
                <p className="case-study__stat-label">Users KYC-verified and bank-linked before wallet activation</p>
              </div>
              <div className="case-study__stat">
                <p className="case-study__stat-value">Real-time</p>
                <p className="case-study__stat-label">Webhook-driven ledger updates + push notifications</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="case-study__section">
          <div className="container">
            <div className="case-study__cta">
              <h2 className="case-study__cta-title">Have a fintech build in mind?</h2>
              <p className="case-study__cta-text">
                Digital wallets, KYC onboarding, bank-as-a-service integrations,
                P2P transfers, payment webhook plumbing — we build regulated
                fintech applications end-to-end. Let's talk.
              </p>
              <Link to="/contact" className="case-study__cta-btn">
                Start Your Project <FiArrowRight />
              </Link>
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

export default PagomigoCaseStudy;
